import { useEffect, useRef, useState } from "react";
import { Bell, BellRing } from "lucide-react";

/**
 * LivePulse — listens to the global `admin:pulse` window event (dispatched by
 * AdminDashboard whenever an SSE event arrives) and shows a small floating
 * animated badge + plays a subtle "ding" sound so the admin notices new
 * activity without staring at the screen.
 *
 * The sound is generated programmatically with the Web Audio API (no asset
 * required, no autoplay-blocked HTML5 <audio> tag).
 */
const PRETTY = {
  message: "💬 Mensaje privado",
  notification: "🔔 Notificación",
  profile_request: "🪪 Solicitud de perfil",
  receipt_uploaded: "🧾 Comprobante subido",
  appointment_changed: "📅 Cita modificada",
  admin_refresh: "✨ Actividad",
};

const scopeLabel = (scope) => {
  switch (scope) {
    case "users": return "👥 Usuarios";
    case "readings": return "🔮 Lecturas";
    case "plans": return "💼 Pagos/Trabajos";
    case "chats_ia": return "🤖 Chat IA";
    default: return null;
  }
};

const playDing = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;

    const make = (freq, delay, dur = 0.18, gain = 0.06) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      g.gain.setValueAtTime(0, now + delay);
      g.gain.linearRampToValueAtTime(gain, now + delay + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, now + delay + dur);
      osc.connect(g).connect(ctx.destination);
      osc.start(now + delay);
      osc.stop(now + delay + dur + 0.02);
    };
    // gentle two-note chime (G5 -> C6)
    make(784, 0);
    make(1046, 0.09);
    // auto-close
    setTimeout(() => ctx.close().catch(() => {}), 600);
  } catch { /* noop */ }
};

const SOUND_KEY = "ca_admin_sound_on";
const useSoundEnabled = () => {
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(SOUND_KEY) !== "0"; } catch { return true; }
  });
  const toggle = () => {
    setEnabled((v) => {
      const next = !v;
      try { localStorage.setItem(SOUND_KEY, next ? "1" : "0"); } catch { /* noop */ }
      return next;
    });
  };
  return [enabled, toggle];
};

export const LivePulse = () => {
  const [pulses, setPulses] = useState([]); // {id, label, ts}
  const [unseen, setUnseen] = useState(0);
  const [open, setOpen] = useState(false);
  const [soundOn, toggleSound] = useSoundEnabled();
  const idRef = useRef(0);
  // throttle sound to avoid spam (500ms)
  const lastSoundAtRef = useRef(0);

  useEffect(() => {
    const onPulse = (e) => {
      const detail = e.detail || {};
      const type = detail.type || "admin_refresh";
      const data = detail.data || {};
      const label = scopeLabel(data.scope) || PRETTY[type] || PRETTY.admin_refresh;
      idRef.current += 1;
      const entry = { id: idRef.current, label, ts: Date.now() };
      setPulses((arr) => [entry, ...arr].slice(0, 20));
      setUnseen((n) => (open ? 0 : n + 1));
      // play subtle sound (throttled)
      if (soundOn && Date.now() - lastSoundAtRef.current > 500) {
        lastSoundAtRef.current = Date.now();
        playDing();
      }
    };
    window.addEventListener("admin:pulse", onPulse);
    return () => window.removeEventListener("admin:pulse", onPulse);
  }, [open, soundOn]);

  // clear unseen counter when opening
  // (avoid setState-in-effect: handled at click site instead)
  const handleToggleOpen = () => {
    setOpen((v) => {
      const next = !v;
      if (next) setUnseen(0);
      return next;
    });
  };

  return (
    <div className="fixed top-4 right-4 z-[120] flex items-start gap-2" data-testid="live-pulse-root">
      <div className="relative">
        <button
          onClick={handleToggleOpen}
          data-testid="live-pulse-toggle"
          className="relative h-11 w-11 rounded-full bg-black/70 border border-[#d4af37]/40 backdrop-blur-sm flex items-center justify-center text-[#d4af37] hover:scale-105 transition-transform"
          title="Actividad en vivo"
        >
          {unseen > 0 ? <BellRing className="w-5 h-5 animate-bounce" /> : <Bell className="w-5 h-5" />}
          {unseen > 0 && (
            <>
              <span data-testid="live-pulse-badge" className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
                {unseen > 99 ? "99+" : unseen}
              </span>
              {/* pulsing ring */}
              <span className="absolute inset-0 rounded-full border-2 border-[#d4af37] animate-ping opacity-60 pointer-events-none" />
            </>
          )}
        </button>

        {open && (
          <div data-testid="live-pulse-panel" className="absolute right-0 mt-2 w-72 max-h-96 overflow-y-auto glass-dark border border-[#d4af37]/30 rounded-2xl p-3 shadow-2xl">
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-xs uppercase tracking-widest text-[#d4af37]">Actividad en vivo</span>
              <button
                data-testid="live-pulse-sound-toggle"
                onClick={toggleSound}
                className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${soundOn ? "border-emerald-500/40 text-emerald-300" : "border-neutral-600 text-neutral-400"}`}
                title="Activar/Silenciar sonido"
              >
                {soundOn ? "🔊 ON" : "🔇 OFF"}
              </button>
            </div>
            {pulses.length === 0 ? (
              <p className="text-xs text-neutral-500 px-2 py-3 text-center">Sin actividad reciente</p>
            ) : (
              <ul className="space-y-1.5">
                {pulses.map((p) => (
                  <li key={p.id} className="text-xs text-neutral-200 bg-black/40 border border-white/5 rounded-lg px-2.5 py-1.5 flex items-center justify-between gap-2">
                    <span className="truncate">{p.label}</span>
                    <span className="text-[10px] text-neutral-500 font-mono whitespace-nowrap">
                      {new Date(p.ts).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <button
              onClick={() => setPulses([])}
              data-testid="live-pulse-clear"
              className="mt-2 w-full text-[10px] uppercase tracking-widest text-neutral-400 hover:text-white py-1"
            >
              Limpiar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
